import * as React from 'react'
import { type ClassicScheme, Presets } from 'rete-react-plugin'
import {
  borderRadius,
  colors,
  fontSize,
  NotificationLevel,
  notify,
  spacing,
  Text,
  Tooltip,
} from '@retailer-platform/shared-components'
import { startCase } from 'lodash'
import styled from '@emotion/styled'
import { IconButton } from '@instacart/ids-customers'
import { ArrowRightIcon, ArrowUpIcon, EditIcon, TrashIcon } from '@instacart/ids-core'
import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDomainMessages } from '../../../../../utils/domain/intl.ts'
import {
  AttributeManagementCategoryEnum,
  type AttributeManagementTypeEnum,
} from '../../../../../types/attributeManagement.types.ts'
import { type Instacart_Catalog_Catflow_V1_StepVersion } from '../../../../../api'
import { type PipelineEditorStepNodeInterface } from '../rete/nodes.tsx'
import PipelineFormStepSelectModal from '../../PipelineFormStepSelectModal.tsx'
import { CAMP_CLIENT_ID } from '../types.ts'
import { Divider } from '../../../../../utils/common.styles.tsx'
import { routes } from '../../../../../routing/routes.ts'
import { ControlOverviewPane } from './control-step-overview-panes/ControlOverviewPane.tsx'

const { RefSocket } = Presets.classic

interface CustomScheme extends ClassicScheme {
  Node: PipelineEditorStepNodeInterface
}

interface Props<S extends CustomScheme> {
  data: S['Node']
  emit: Presets.classic.RenderEmit<S>
}

const PipelineNodeSocketContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'start',
  alignItems: 'anchor-center',
  '[title="socket"]': {
    border: `1px solid #6b895f66`,
    borderStyle: `ridge`,
    borderWidth: `4px`,
    '&:hover': {
      border: `1px solid #6b895fa1`,
      borderStyle: `ridge`,
    },
  },
})

const PipelineNodeCard = styled.div({
  display: 'flex',
  justifyItems: `center`,
  alignItems: `center`,
  justifyContent: 'left',
  flex: 1,
  position: 'relative',
  flexDirection: 'column',
  background: colors.SECONDARY.LIGHT,
  border: `2px solid ${colors.GRAYSCALE.X30}`,
  borderRadius: borderRadius.X4,
  width: 'fit-content !important',
  height: 'fit-content !important',
  boxShadow: `0 5px 5px rgba(0, 0, 0, 0.15)`,
})

export const StepCategoryColors: { [category: string]: string } = {
  [AttributeManagementCategoryEnum.Control]: '#000000',
  [AttributeManagementCategoryEnum.DataManipulation]: '#FF0000',
  [AttributeManagementCategoryEnum.Pipeline]: '#007BFF',
  [AttributeManagementCategoryEnum.Input]: '#28A745',
  [AttributeManagementCategoryEnum.Audit]: '#FFC107',
  [AttributeManagementCategoryEnum.ExtractionModel]: '#6F42C1',
  [AttributeManagementCategoryEnum.Publish]: '#FD7E14',
}

const PipelineNodeCardHeader = styled.div(
  ({ category }: { category: AttributeManagementCategoryEnum }) => ({
    background: `${StepCategoryColors?.[category.toUpperCase() as string] || 'black'}`,
    width: '100%',
    height: `9px`,
    borderBottom: `2px solid #00000047`,
    borderTopLeftRadius: `2px`,
    borderTopRightRadius: `2px`,
  })
)

const PipelineNodeCardSection = styled.div({
  display: 'flex',
  flexDirection: 'column',
  borderBottom: `1px solid ${colors.GRAYSCALE.X30}`,
  padding: `${spacing.X8}`,
  alignItems: 'center',
  whiteSpace: `nowrap`,
  width: '100%',
  gap: `${spacing.X4}`,
})

export const PipelineNodeCardPill = styled.div({
  width: `fit-content`,
  background: `#ffffff`,
  border: `1px solid ${colors.GRAYSCALE.X30}`,
  borderRadius: `${borderRadius.X8}`,
  display: 'flex',
  flexDirection: `column`,
  alignItems: 'center',
  gap: `${spacing.X8}`,
  margin: `${spacing.X8}`,
  padding: `${spacing.X16}`,
})

export const OverrideGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr auto auto auto',
  gap: spacing.X8,
})

export const TextRight = styled.div({
  textAlign: 'right',
})

const PipelineNodeCardContentSocketSide = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  gap: `${spacing.X8}`,
})

const PipelineNodeCardContentSocketContainer = styled.div({
  justifyContent: 'space-between',
  padding: `${spacing.X8}`,
  flexDirection: `row`,
  display: 'flex',
  width: '100%',
})

const PipelineNodeCardFooter = styled.div({
  display: 'flex',
  flexDirection: 'row',
  borderTop: `1px solid ${colors.GRAYSCALE.X30}`,
  padding: `${spacing.X8}`,
  alignItems: 'center',
  whiteSpace: `nowrap`,
  width: '100%',
  justifyContent: 'center',
})

export function PipelineEditorNode<S extends CustomScheme>(props: Props<S>) {
  const [showEditModal, setShowEditModal] = useState(false)

  const i18n = useDomainMessages({
    deleteStep:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.deleteStep',
    editStep:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.editStep',
    updateToLatestVersion:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepTile.updateToLatestVersion',
    updateToLatestVersionSuccess:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepTile.updateToLatestVersionSuccess',
    errorMessage:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.errorMessage',
  })

  const { data: node } = props
  const {
    step,
    stepVersion: stepVersionObject,
    props: { disabled, onDelete, onChange, showDebugTooltips, getStepVersionsCache },
  } = props.data.input

  const inputs = Object.entries(node.inputs)
  const outputs = Object.entries(node.outputs)
  const stepOverrides: Record<string, any> = step?.attributeOverrides ?? {}

  // Step info
  const stepVersion = step?.version ?? ''
  const stepId = step?.stepId || ''
  const stepName = stepVersionObject?.step?.name

  // Get step versions from cache
  const stepVersionsData = getStepVersionsCache ? getStepVersionsCache(stepId) : undefined

  const availableStepVersions: Instacart_Catalog_Catflow_V1_StepVersion[] = useMemo(
    () =>
      stepVersionsData?.instacart_catalog_catflow_v1_CatflowAPI_ListStepVersions?.stepVersions
        ?.filter(v => !v.tombstoned)
        ?.sort((a, b) => parseInt(b.version ?? '0') - parseInt(a.version ?? '0')) || [],
    [stepVersionsData]
  )
  const latestAvailableVersion =
    availableStepVersions.length > 0 ? availableStepVersions[0] : stepVersionObject
  const stepType = (stepVersionObject?.step?.type ??
    step?.nextStepsRule?.type ??
    '') as AttributeManagementTypeEnum
  const stepCategory = (
    stepVersionObject?.step?.stepCategory ??
    step?.type ??
    AttributeManagementCategoryEnum.Unknown
  ).toUpperCase() as AttributeManagementCategoryEnum
  const isControlStep = stepCategory === AttributeManagementCategoryEnum.Control

  const isCurrentVersionLatest = useMemo(
    () => latestAvailableVersion?.version === stepVersion,
    [latestAvailableVersion?.version, stepVersion]
  )

  const basePath = routes['app-admin-catalog-admin-attribute-management-details']
  const stepUrl = stepId ? `${basePath}?stepId=${stepId}` : null

  const handleUpdateToLatestVersion = () => {
    if (!latestAvailableVersion?.version) return

    onChange({
      ...step,
      stepVersionId: latestAvailableVersion?.id ?? '',
      version: latestAvailableVersion?.version ?? '',
    })

    notify({
      level: NotificationLevel.Success,
      contents: i18n.updateToLatestVersionSuccess,
    })
  }

  const nodeCard = (
    <PipelineNodeCard id={props.data.id}>
      {showEditModal &&
        createPortal(
          <PipelineFormStepSelectModal
            step={step}
            category={stepCategory || null}
            onClose={() => {
              setShowEditModal(false)
            }}
            onConfirm={updatedStep => {
              onChange({
                ...step,
                ...updatedStep,
              })
              setShowEditModal(false)
            }}
          />,
          document.body
        )}
      <PipelineNodeCardHeader category={stepCategory} />
      <PipelineNodeCardSection>
        <Text size="large" weight="bold">
          {startCase(stepCategory)}
        </Text>
        <Text size="medium" weight="semibold">
          {startCase(stepType)}
        </Text>
      </PipelineNodeCardSection>
      <PipelineNodeCardContentSocketContainer>
        <PipelineNodeCardContentSocketSide>
          {inputs.map(
            ([key, input]) =>
              input && (
                <PipelineNodeSocketContainer
                  className="input"
                  key={key}
                  data-testid={`input-${key}`}
                >
                  <RefSocket
                    name="input-socket"
                    side="input"
                    emit={props.emit}
                    socketKey={key}
                    nodeId={node.id}
                    payload={input.socket}
                  />
                </PipelineNodeSocketContainer>
              )
          )}
        </PipelineNodeCardContentSocketSide>
        {isControlStep ? (
          <ControlOverviewPane step={step} />
        ) : (
          <PipelineNodeCardPill>
            {stepName && stepVersion && (
              <>
                <a
                  onPointerDown={e => e.stopPropagation()}
                  rel="noreferrer"
                  href={stepUrl ?? '#'}
                  target={'_blank'}
                  style={{ color: colors.PRIMARY.EXTRA_DARK }}
                >
                  <Text size="small" weight="semibold">
                    {stepName}
                  </Text>
                </a>
                <Text size="small" weight="regular">
                  Version {stepVersion}
                </Text>
                <Divider />
              </>
            )}

            <Text size={'small'} weight="semibold">
              Overrides
            </Text>
            {Object.keys(stepOverrides || {}).length === 0 ? (
              <Text size="small" weight="regular">
                No overrides
              </Text>
            ) : (
              <OverrideGrid>
                {Object.keys(stepOverrides || {}).map(key => (
                  <React.Fragment key={key}>
                    <TextRight>
                      <Text size="small" weight="regular">
                        {key}:
                      </Text>
                    </TextRight>

                    <TextRight>
                      {JSON.stringify(stepVersionObject?.attributes?.[key] || 'null')}
                    </TextRight>
                    <ArrowRightIcon size={fontSize.X14} />
                    <div>{JSON.stringify(stepOverrides?.[key] ?? 'null')}</div>
                  </React.Fragment>
                ))}
              </OverrideGrid>
            )}
          </PipelineNodeCardPill>
        )}

        <PipelineNodeCardContentSocketSide>
          {outputs.map(
            ([key, output]) =>
              output && (
                <PipelineNodeSocketContainer
                  className="output"
                  key={key}
                  data-testid={`output-${key}`}
                >
                  {output?.label ? output.label : null}
                  <RefSocket
                    name="output-socket"
                    side="output"
                    emit={props.emit}
                    socketKey={key}
                    nodeId={node.id}
                    payload={output.socket}
                  />
                </PipelineNodeSocketContainer>
              )
          )}
        </PipelineNodeCardContentSocketSide>
      </PipelineNodeCardContentSocketContainer>

      <PipelineNodeCardFooter>
        <IconButton
          disabled={disabled}
          icon={TrashIcon}
          accessibleLabel={i18n.deleteStep}
          onPress={() => {
            onDelete(step)
          }}
        />
        <IconButton
          disabled={disabled}
          icon={EditIcon}
          accessibleLabel={i18n.editStep}
          onPress={() => {
            setShowEditModal(true)
          }}
        />
        <IconButton
          disabled={disabled || isCurrentVersionLatest || !stepVersion}
          icon={ArrowUpIcon}
          accessibleLabel={i18n.updateToLatestVersion}
          onPress={handleUpdateToLatestVersion}
        />
      </PipelineNodeCardFooter>
    </PipelineNodeCard>
  )

  if (!showDebugTooltips) {
    return nodeCard
  }

  return (
    <Tooltip target={nodeCard}>
      <pre
        style={{
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'break-word',
        }}
      >
        {JSON.stringify(node.input?.step, null, 2)}
      </pre>
    </Tooltip>
  )
}
